import type { TaskResponse, TaskTypeResponse } from '@erp/rpc';
import type { TaskTypeViewRegion } from '@erp/shared-types';
import {
  resolveTaskTypeViewSlot,
  taskInputRecord,
  taskWorkingCopy,
  type TaskTypeViewSlot,
  type TaskViewSchemaField,
} from './taskTypeViewValue';
import { useTaskViewCommit } from './useTaskViewCommit';
import type { CorrectionCommitResult } from './useCommitTaskCorrection';

export type TaskViewState = {
  slot: TaskTypeViewSlot;
  schemaFields: readonly TaskViewSchemaField[];
  input: Record<string, unknown>;
  workingCopy: Record<string, unknown>;
  anchorType: string | null;
  anchorId: string | null;
  commit: (next: Record<string, unknown>) => Promise<CorrectionCommitResult>;
  /** True while a supplier change is rematching rows; the view is inert. */
  rematching: boolean;
  /** Regions for the left column, in spec order. */
  mainRegions: readonly TaskTypeViewRegion[];
  /** Regions for the side panel tabs, in spec order. */
  asideRegions: readonly TaskTypeViewRegion[];
};

/**
 * Everything the review page needs to render a type's view, resolved once
 * so the main column and the side panel share one working copy and one
 * commit (which carries the supplier-line rematch).
 */
export function useTaskViewState(
  task: TaskResponse,
  taskType: TaskTypeResponse | undefined,
): TaskViewState {
  const slot = resolveTaskTypeViewSlot(taskType);
  const workingCopy = taskWorkingCopy(task);
  const { commit, rematching } = useTaskViewCommit(task, slot, workingCopy);
  const regions = slot.kind === 'ready' ? slot.view.regions : [];
  return {
    slot,
    schemaFields: taskType?.inputSchema?.fields ?? [],
    input: taskInputRecord(task.input),
    workingCopy,
    anchorType: task.anchorEntityType,
    anchorId: task.anchorEntityId,
    commit,
    rematching,
    mainRegions: regions.filter((region) => region.column === 'main'),
    asideRegions: regions.filter((region) => region.column === 'aside'),
  };
}
